import { useState, useEffect, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import ReactMarkdown from 'react-markdown'
import { useChat } from '../../context/ChatContext'
import profile from '../../data/profiles/activeProfile'
import './index.scss'

const { widgetTitle, suggestedPrompts, placeholder, disclaimer } = profile.ai

const TOPICS = [
  { label: 'Core skills', prompt: "What are Vamsi's core technical skills and his complete tech stack?" },
  { label: 'Work history', prompt: "Tell me about Vamsi's professional work history and key roles." },
  { label: 'Projects', prompt: 'What are some key projects Vamsi has built and delivered?' },
  { label: 'AI experience', prompt: 'What AI-powered and RAG interfaces has Vamsi built?' },
  { label: 'Education', prompt: "What is Vamsi's academic and educational background?" },
  { label: 'Contact', prompt: 'How can I contact Vamsi for professional opportunities?' },
]

/* Brand name with the last word set in italic amber */
const words = widgetTitle.split(' ')
const brandLead = words.slice(0, -1).join(' ')
const brandEm = words[words.length - 1]

/* ─── Transcript exchange ───────────────────────────── */
const Exchange = ({ msg }) =>
  msg.role === 'user' ? (
    <div className="ai-q">
      <span className="ai-q__marker" aria-hidden="true">
        Q.
      </span>
      <div className="ai-q__body">
        <span className="ai-speaker">You asked</span>
        <p>{msg.text}</p>
      </div>
    </div>
  ) : (
    <div className="ai-a">
      <span className="ai-a__marker" aria-hidden="true">
        A.
      </span>
      <div className="ai-a__body">
        <span className="ai-speaker">
          {widgetTitle} <i>· Claude</i>
        </span>
        <div className="ai-a__prose">
          <ReactMarkdown>{msg.text}</ReactMarkdown>
        </div>
      </div>
    </div>
  )

/* ─── Composing indicator ───────────────────────────── */
const Composing = () => (
  <div className="ai-a ai-a--composing">
    <span className="ai-a__marker" aria-hidden="true">
      A.
    </span>
    <div className="ai-a__body">
      <span className="ai-speaker">
        {widgetTitle} <i>· Claude</i>
      </span>
      <p className="ai-composing">
        composing an answer
        <span className="ai-composing__cursor" />
      </p>
    </div>
  </div>
)

/* ─── Main component ────────────────────────────────── */
const AiAssistant = () => {
  const [input, setInput] = useState('')
  const { messages, isLoading, sendMessage, clearMessages } = useChat()
  const bottomRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isLoading])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!input.trim() || isLoading) return
    sendMessage(input.trim())
    setInput('')
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const isOnlyWelcome = messages.length === 1 && messages[0].role === 'ai'

  return (
    <div className="ai-page">
      {/* ── Mobile top bar ── */}
      <div className="ai-topbar">
        <div className="ai-topbar__brand">
          <span className={`ai-live${isLoading ? ' ai-live--busy' : ''}`} />
          <span className="ai-topbar__name">
            {brandLead} <em>{brandEm}</em>
          </span>
        </div>
        <button
          className="ai-topbar__clear"
          onClick={clearMessages}
          disabled={isOnlyWelcome}
          aria-label="Clear conversation"
        >
          <FontAwesomeIcon icon={faTrashAlt} />
        </button>
      </div>

      {/* ── Left panel ── */}
      <aside className="ai-panel">
        <p className="ai-kicker">
          <span className="ai-kicker__rule" />
          The Interview
        </p>

        <h1 className="ai-brand">
          {brandLead} <em>{brandEm}</em>
        </h1>

        <p className="ai-powered">
          <span className={`ai-live${isLoading ? ' ai-live--busy' : ''}`} />
          powered by Claude
        </p>

        <div className="ai-panel__divider" />

        <p className="ai-panel__label">Lines of inquiry</p>
        <nav className="ai-topics">
          {TOPICS.map((t, i) => (
            <button
              key={t.label}
              className="ai-topic"
              onClick={() => sendMessage(t.prompt)}
              disabled={isLoading}
              data-cursor="ask"
            >
              <span className="ai-topic__num">
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="ai-topic__label">{t.label}</span>
              <span className="ai-topic__tick" />
            </button>
          ))}
        </nav>

        <div className="ai-panel__foot">
          <button
            className="ai-clear"
            onClick={clearMessages}
            disabled={isOnlyWelcome}
          >
            <FontAwesomeIcon icon={faTrashAlt} />
            <span>Clear session</span>
          </button>
          <p className="ai-disclaimer">{disclaimer}</p>
        </div>
      </aside>

      {/* ── Transcript ── */}
      <main className="ai-main">
        <span className="ai-ghost" aria-hidden="true">
          Q&amp;A
        </span>

        <div className="ai-thread">
          <header className="ai-masthead">
            <h2 className="ai-masthead__title">
              The <em>interview.</em>
            </h2>
            <p className="ai-masthead__sub">
              An AI stand-in, briefed on the full résumé — ask it anything a
              first call would cover.
            </p>
          </header>

          {messages.map((msg, i) => (
            <Exchange key={i} msg={msg} />
          ))}

          {isLoading && <Composing />}

          {isOnlyWelcome && (
            <div className="ai-suggest">
              <h3 className="ai-rule-title">
                <span className="ai-rule-title__index">?</span>
                <span className="ai-rule-title__text">Suggested questions</span>
                <span className="ai-rule-title__line" />
              </h3>
              {suggestedPrompts.map((s, i) => (
                <button
                  key={s}
                  className="ai-suggest__row"
                  style={{ '--i': i }}
                  onClick={() => sendMessage(s)}
                  data-cursor="ask"
                >
                  <span className="ai-suggest__num">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="ai-suggest__text">{s}</span>
                  <span className="ai-suggest__arrow" aria-hidden="true">
                    ↗
                  </span>
                </button>
              ))}
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* ── Compose ── */}
        <div className="ai-compose">
          <form className="ai-compose__form" onSubmit={handleSubmit}>
            <span className="ai-compose__prompt" aria-hidden="true">
              &gt;
            </span>
            <textarea
              ref={inputRef}
              className="ai-compose__input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={isLoading}
              rows={1}
            />
            <button
              type="submit"
              className="ai-compose__send"
              disabled={isLoading || !input.trim()}
              aria-label="Send"
              data-cursor="send"
            >
              ↗
            </button>
          </form>
          <p className="ai-compose__hint">
            Enter to send · Shift+Enter for a new line
          </p>
        </div>
      </main>
    </div>
  )
}

export default AiAssistant
